import React from "react";
import { IoBagHandleOutline } from "react-icons/io5";
import successIcon from "../../assets/images/icons/ads-submitted-success.svg";

const PLACEMENT_LABELS = {
  searchResult: "Search Result",
  marketplaceFeed: "Home Feed",
  categoryResults: "Category Results",
};

const OBJECTIVE_LABELS = {
  product_listing_views: "Product Listing Views",
  marketplace_conversions: "Marketplace Conversions",
};

const daysBetween = (start, end) =>
  start && end ? Math.max(1, Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24))) : null;

// Reflects the real ad status the API returned - promoteListing calls submitAdForReview
// automatically, but that can soft-fail (moderation queue down), leaving the ad in Draft.
const buildTimelineSteps = (submitted) => [
  { label: "Draft", state: "done" },
  { label: "Submitted", state: submitted ? "current" : "pending" },
  { label: "In Review", state: submitted ? "upcoming" : "pending" },
  { label: "Approved", state: "pending" },
];

const TimelineDot = ({ state }) => {
  if (state === "done") {
    return (
      <span className="w-9 h-9 rounded-full bg-[#39b54a] flex items-center justify-center shrink-0">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="w-9 h-9 rounded-full border-2 border-[#39b54a] flex items-center justify-center shrink-0">
        <span className="w-4 h-4 rounded-full bg-[#39b54a]" />
      </span>
    );
  }
  if (state === "upcoming") {
    return (
      <span className="w-9 h-9 rounded-full bg-[#e05a4e] flex items-center justify-center shrink-0">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
      </span>
    );
  }
  return <span className="w-9 h-9 rounded-full border-2 border-[#d9d9d9] shrink-0" />;
};

export default function PromoteSubmittedSuccess({ product, formData, submissionResult, onGoToAdManager, onCreateAnother }) {
  const campaign = submissionResult?.campaign;
  const productImage = product?.product_media?.[0]?.url || "/image/default.png";

  // Real, persisted values from the campaign the API actually created - not recomputed from
  // local form state, since that's just what was typed, not what got saved.
  const days = daysBetween(campaign?.start_date, campaign?.end_date) || 1;
  const totalBudget = Number(campaign?.budget || 0);
  const dailyBudget = totalBudget / days;
  const selectedPlacements = Object.entries(formData.placements)
    .filter(([, checked]) => checked)
    .map(([key]) => PLACEMENT_LABELS[key]);
  const submittedAt = campaign?.created_at ? new Date(campaign.created_at) : new Date();
  const isSubmitted = submissionResult?.ad?.status === "Submitted for Review";
  const timelineSteps = buildTimelineSteps(isSubmitted);

  return (
    <div>
      <div className="text-center mb-6">
        <img src={successIcon} alt="" className="w-[110px] h-[110px] mx-auto mb-2" />
        <h1 className="text-xl font-bold mb-1">Ad Campaign<br />Submitted Successfully!</h1>
        <p className="text-sm text-[#747474]">
          {isSubmitted
            ? "Your ad is currently in review. Our team typically processes approvals within 1-2 hours."
            : "Your campaign was created, but the ad couldn't be submitted for review yet - you'll need to submit it manually."}
        </p>
        {submissionResult?.targetingError && (
          <p className="text-xs text-orange-600 mt-2">
            Note: your targeting/placement preferences couldn't be saved ({submissionResult.targetingError}). The campaign itself was created successfully.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#f7f7f7] rounded-2xl p-5 mb-6">
        <div>
          <p className="font-semibold text-black mb-1">Campaign Summary</p>
          <p className="text-sm text-[#434656] mb-4">{OBJECTIVE_LABELS[campaign?.objective] || OBJECTIVE_LABELS[formData.objective]}</p>

          <div className="bg-white rounded-xl p-3 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Budget type</span>
              <span className="bg-[#095228] text-white text-xs font-semibold px-3 py-1 rounded-full capitalize">
                {formData.budgetType} Budget
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 mb-3 grid grid-cols-3 gap-2">
            <div>
              <p className="text-xs text-[#747474]">Total Budget</p>
              <p className="font-semibold">$ {totalBudget.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-[#747474]">Daily Budget</p>
              <p className="font-semibold">$ {dailyBudget.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-[#747474]">Duration</p>
              <p className="font-semibold">{days} Days</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 mb-3 flex items-center justify-between">
            <span className="text-sm text-[#434656]">Schedule</span>
            <span className="text-sm font-semibold">{campaign?.start_date} - {campaign?.end_date}</span>
          </div>

          <div className="bg-white rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm text-[#434656]">Placements</span>
            <span className="text-sm font-semibold">{selectedPlacements.join(", ")}</span>
          </div>
        </div>

        <div>
          <p className="font-semibold text-black mb-1">ad Preview</p>
          <div className="relative bg-white rounded-lg shadow-sm mt-4">
            <div className="w-full h-[150px]">
              <img src={productImage} alt={product?.product_name} className="w-full h-full object-contain rounded-t-lg" />
            </div>
            {product?.offer_percentage ? (
              <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                {product.offer_percentage}% Offer
              </span>
            ) : null}
            <span className="absolute top-2 right-2 bg-[#ff8a3d] text-white text-xs font-bold px-2 py-1 rounded">
              Sponsored
            </span>
            <div className="p-2">
              <p className="text-[11px] text-[#095228] font-semibold mb-1">{product?.seller?.name || "Your Store"}</p>
              <p className="text-sm text-gray-600 uppercase line-clamp-2">{product?.product_name}</p>
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="line-through text-gray-400 text-sm">${product?.product_price}</span>
                  <span className="text-black font-semibold">${product?.discount_price}</span>
                </div>
                <button className="mt-3 flex py-2 px-3 text-[13px] gap-1.5 items-center bg-gray-900 text-white rounded-full">
                  <IoBagHandleOutline />
                  ADD TO CART
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-start mb-8">
        {timelineSteps.map((step, i) => (
          <div key={step.label} className={`flex flex-col items-center ${i < timelineSteps.length - 1 ? "flex-1" : ""}`}>
            <div className="flex items-center w-full">
              {i > 0 && <span className={`h-0.5 flex-1 ${timelineSteps[i - 1].state === "done" || timelineSteps[i - 1].state === "current" ? "bg-[#39b54a]" : "bg-[#d9d9d9]"}`} />}
              <TimelineDot state={step.state} />
              {i < timelineSteps.length - 1 && <span className={`h-0.5 flex-1 ${step.state === "done" ? "bg-[#39b54a]" : "bg-[#d9d9d9]"}`} />}
            </div>
            <p className={`text-sm font-semibold mt-2 ${step.state === "done" || step.state === "current" ? "text-[#39b54a]" : "text-[#9a9a9a]"}`}>
              {step.label}
            </p>
            {(step.state === "done" || step.state === "current") && (
              <p className="text-xs text-[#747474]">{submittedAt.toLocaleDateString()} | {submittedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onGoToAdManager}
        className="w-full bg-[#095228] text-white font-semibold py-3 rounded-full mb-3"
      >
        Go to Ad Manager
      </button>
      <button
        type="button"
        onClick={onCreateAnother}
        className="w-full bg-[#ebf8ed] border border-[#095228] text-[#095228] font-semibold py-3 rounded-full"
      >
        Create Another Ad
      </button>
    </div>
  );
}
